import { EJECT, FOOD, TEAM_HUES, WORLD } from '../constants';
import { mulberry32, randRange } from '../utils/rng';

export interface FoodPellet {
  id: number;
  x: number;
  y: number;
  /** Velocity (world u/s). 0 for static pellets, nonzero while ejected. */
  vx: number;
  vy: number;
  radius: number;
  mass: number;
  /** Hue — always snapped to one of TEAM_HUES so color match reads cleanly. */
  hue: number;
  /** Per-pellet animation phase (breathing pulse). */
  phase: number;
  /** Lifespan (sec). +Infinity for static pellets, finite for ejected. */
  ttl: number;
  /** Sim time before which this pellet cannot be eaten (eject grace). */
  eatableAt: number;
  /** Snake id that ejected this pellet, or 0 for static. */
  ownerId: number;
}

/**
 * FoodField — owns the static pellet pool + handles ejected projectile pellets.
 *
 * Static count is sized once at construction:
 *   count = min(MAX_PELLETS, floor(WORLD² / DENSITY_DIVISOR))
 *
 * Ejected pellets are not separate objects — they reuse a slot in the same
 * pool. We pick a static pellet far from the player's view, repurpose it as
 * a moving projectile for EJECT.LIFE seconds, and then it respawns as static.
 */
export class FoodField {
  public readonly pellets: FoodPellet[] = [];
  private nextId = 1;
  private readonly rng: () => number;

  constructor(seed: number) {
    this.rng = mulberry32(seed);
    const target = Math.min(
      FOOD.MAX_PELLETS,
      Math.floor((WORLD.SIZE * WORLD.SIZE) / FOOD.DENSITY_DIVISOR),
    );
    for (let i = 0; i < target; i++) this.pellets.push(this.makeStatic());
  }

  private pickTeamHue(): number {
    return TEAM_HUES[Math.floor(this.rng() * TEAM_HUES.length)]!;
  }

  private makeStatic(): FoodPellet {
    const r = randRange(this.rng, FOOD.MIN_RADIUS, FOOD.MAX_RADIUS);
    return {
      id: this.nextId++,
      x: randRange(this.rng, WORLD.EDGE_PADDING, WORLD.SIZE - WORLD.EDGE_PADDING),
      y: randRange(this.rng, WORLD.EDGE_PADDING, WORLD.SIZE - WORLD.EDGE_PADDING),
      vx: 0,
      vy: 0,
      radius: r,
      mass:
        FOOD.MASS_PER_PELLET_MIN +
        ((r - FOOD.MIN_RADIUS) / (FOOD.MAX_RADIUS - FOOD.MIN_RADIUS)) *
          (FOOD.MASS_PER_PELLET_MAX - FOOD.MASS_PER_PELLET_MIN),
      hue: this.pickTeamHue(),
      phase: this.rng() * Math.PI * 2,
      ttl: Infinity,
      eatableAt: 0,
      ownerId: 0,
    };
  }

  /** Respawn pellet i as a fresh static pellet at a random world location. */
  respawn(i: number): void {
    const p = this.pellets[i];
    if (!p) return;
    const fresh = this.makeStatic();
    p.x = fresh.x;
    p.y = fresh.y;
    p.vx = 0;
    p.vy = 0;
    p.radius = fresh.radius;
    p.mass = fresh.mass;
    p.hue = fresh.hue;
    p.phase = fresh.phase;
    p.ttl = Infinity;
    p.eatableAt = 0;
    p.ownerId = 0;
  }

  /**
   * Repurpose a pellet far from the action as an ejected projectile.
   * Used by World on a successful eject. We pick the pellet farthest from
   * the ejector's head so we don't spawn-snap a pellet out of someone's view.
   */
  ejectAt(opts: {
    fromX: number;
    fromY: number;
    headingX: number;
    headingY: number;
    hue: number;
    mass: number;
    ownerId: number;
    now: number;
  }): void {
    // Pick a far-away static pellet to "borrow". Avoid touching ejected ones.
    let bestIdx = -1;
    let bestD2 = -Infinity;
    const pellets = this.pellets;
    const sampleCount = Math.min(40, pellets.length);
    // Random sampling — bench: O(40) sample is fine vs O(N) full scan at N=1500.
    for (let k = 0; k < sampleCount; k++) {
      const idx = Math.floor(this.rng() * pellets.length);
      const p = pellets[idx]!;
      if (p.ttl !== Infinity) continue;
      const dx = p.x - opts.fromX;
      const dy = p.y - opts.fromY;
      const d2 = dx * dx + dy * dy;
      if (d2 > bestD2) {
        bestD2 = d2;
        bestIdx = idx;
      }
    }
    if (bestIdx < 0) return;
    const p = pellets[bestIdx]!;
    p.x = opts.fromX;
    p.y = opts.fromY;
    p.vx = opts.headingX * EJECT.SPEED;
    p.vy = opts.headingY * EJECT.SPEED;
    p.radius = (FOOD.MIN_RADIUS + FOOD.MAX_RADIUS) * 0.55;
    p.mass = opts.mass;
    p.hue = opts.hue;
    p.phase = 0;
    p.ttl = EJECT.LIFE;
    p.eatableAt = opts.now + EJECT.GRACE;
    p.ownerId = opts.ownerId;
  }

  /**
   * Repurpose a pellet at a death-drop / boost-trail location.
   *
   * Static drops (no velocity): used for boost trail — pellets sit where
   * dropped so the snake leaves a visible breadcrumb path.
   *
   * Flying drops (with vx/vy): used for death bursts — pellets fly briefly
   * in the given direction before drag slows them to rest. They keep
   * `ttl = Infinity` so they don't expire after settling — anyone can eat.
   */
  dropAt(opts: {
    x: number; y: number; hue: number; mass: number;
    vx?: number; vy?: number;
    /** Snake id that created this pellet (boost-trail / death-burst).
     *  Defaults to 0 = anonymous. The eating logic checks this so a
     *  snake can't farm its own trail for a profit — own pellets are
     *  treated as off-color regardless of hue. */
    ownerId?: number;
  }): void {
    let bestIdx = -1;
    let bestD2 = -Infinity;
    const pellets = this.pellets;
    const sampleCount = Math.min(30, pellets.length);
    for (let k = 0; k < sampleCount; k++) {
      const idx = Math.floor(this.rng() * pellets.length);
      const p = pellets[idx]!;
      if (p.ttl !== Infinity) continue;
      const dx = p.x - opts.x;
      const dy = p.y - opts.y;
      const d2 = dx * dx + dy * dy;
      if (d2 > bestD2) {
        bestD2 = d2;
        bestIdx = idx;
      }
    }
    if (bestIdx < 0) return;
    const p = pellets[bestIdx]!;
    p.x = opts.x + (this.rng() - 0.5) * 16;
    p.y = opts.y + (this.rng() - 0.5) * 16;
    p.vx = opts.vx ?? 0;
    p.vy = opts.vy ?? 0;
    p.radius = Math.min(FOOD.MAX_RADIUS * 1.2, FOOD.MIN_RADIUS + Math.sqrt(opts.mass) * 0.4);
    p.mass = Math.min(opts.mass, FOOD.MASS_PER_PELLET_MAX * 2);
    p.hue = opts.hue;
    p.phase = this.rng() * Math.PI * 2;
    p.ttl = Infinity;
    p.eatableAt = 0;
    p.ownerId = opts.ownerId ?? 0;
  }

  /**
   * Advance pellet physics:
   *   - ANY pellet with nonzero velocity gets dragged regardless of ttl.
   *     That's how death-burst pellets (ttl = Infinity, but flying) settle.
   *   - Finite-ttl pellets (ejected) also age and respawn when expired.
   *   - Pellets close to fully stopped have velocity zeroed to avoid
   *     infinitesimal jitter and remove them from the hot path.
   */
  step(dt: number, now: number): void {
    const pellets = this.pellets;
    const dragFactor = Math.exp(-EJECT.DRAG * dt);
    for (let i = 0; i < pellets.length; i++) {
      const p = pellets[i]!;
      const moving = p.vx !== 0 || p.vy !== 0;

      if (moving) {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= dragFactor;
        p.vy *= dragFactor;
        // Settle: zero out residual velocity once it's negligible.
        if (Math.abs(p.vx) < 1.5 && Math.abs(p.vy) < 1.5) {
          p.vx = 0;
          p.vy = 0;
        }
        // Containment — soft clamp at world edges.
        if (p.x < WORLD.EDGE_PADDING) { p.x = WORLD.EDGE_PADDING; p.vx = 0; }
        if (p.x > WORLD.SIZE - WORLD.EDGE_PADDING) { p.x = WORLD.SIZE - WORLD.EDGE_PADDING; p.vx = 0; }
        if (p.y < WORLD.EDGE_PADDING) { p.y = WORLD.EDGE_PADDING; p.vy = 0; }
        if (p.y > WORLD.SIZE - WORLD.EDGE_PADDING) { p.y = WORLD.SIZE - WORLD.EDGE_PADDING; p.vy = 0; }
      }

      if (p.ttl !== Infinity) {
        p.ttl -= dt;
        if (p.ttl <= 0) this.respawn(i);
      }
      void now;
    }
  }
}
